import { addDays, format, formatDuration, intervalToDuration, isValid, parse, startOfDay, subDays } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

export const BudapestTimezone = 'Europe/Budapest';
const NewYorkTimezone = 'America/New_York';

/**
 * Gets date from now
 * for example, today is 2011-10-05 13:02:00 and you call
 *
 *  getDateDaysFromNow(-2) -> 2011-10-03 00:00:00
 */
export const getDateDaysFromNow = (days: number) => {
  const date = new Date();
  const res = days < 0 ? subDays(date, Math.abs(days)) : addDays(date, days);
  return startOfDay(res);
};

/**
 * Formats date to eStream format
 * @param date
 */
export const formatDateTimeToEStream = (date: Date) => {
  return format(date, `yyyy-MM-dd'T'HH:mm:ssxxx`);
};

/**
 * Parses date time from eStream chart response
 * @param date For example 2024-07-01T09:13:00+02:00
 * @returns Date object as 2024-07-01T09:13:00+00:00
 */
export const parseDateFromEStreamChart = (date: string | Date): Date => {
  if (typeof date === 'string') {
    return new Date(Date.parse(date));
  } else {
    return date;
  }
};

export const createDateFromEStreamStream = (pDate?: string, pTime?: string): Date => {
  const date = pDate ? pDate : format(new Date(), SERVER_DATE_FORMAT);
  const time = pTime ? pTime : '00:00:00.000';
  return new Date(Date.parse(`${date} ${time} UTC`));
};

/**
 * Formats date to day format for request
 * @param date
 */
export const formatDateToRequestDay = (date: Date) => {
  return format(date, SERVER_DATE_FORMAT);
};

export const timeConsts = {
  SECONDS_10: 10 * 1000,
  SECONDS_20: 20 * 1000,
  SECONDS_30: 30 * 1000,
  MINUTE: 60 * 1000,
  MINUTE_15: 15 * 60 * 1000,
  HOUR: 60 * 60 * 1000,
  HOUR_4: 4 * 60 * 60 * 1000,
};

export const formatDateDiff = (start: Date, end: Date): string => {
  return formatDuration(
    intervalToDuration({
      start,
      end,
    }),
  );
};

export const getDateFromDateTime = (dateTime?: string): Date | undefined => {
  if (!dateTime) {
    return undefined;
  }
  return new Date(dateTime.split('T')[0]);
};

/**
 * Returns true if the given date is in the ERSTE allowed premarket time of the USA market (NewYork timezone's 7:15-9:30 am).
 * @param date
 */
export const isUsaPremarketTime = (date?: Date) => {
  const now = date || new Date();
  const newYorkTime = new Intl.DateTimeFormat('en-US', {
    timeZone: NewYorkTimezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(now);
  const [hour, minute] = newYorkTime.split(':').map(Number);

  // returns true if the given date is between 7:15 and 9:30 in the morning
  return (hour === 7 && minute >= 15) || hour === 8 || (hour === 9 && minute <= 30);
};

/**
 * Returns the given date's time in hours:minutes at Budapest timezone.
 */
export const getDateTimeInHoursAndMinuteAtBudapestTZ = (date?: Date): [number, number] => {
  const budapestTime = new Intl.DateTimeFormat('hu-HU', {
    timeZone: BudapestTimezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  const [hour, minute] = budapestTime.split(':').map(Number);
  return [hour, minute];
};

/**
 * Returns the given date's time in hours at Budapest timezone.
 */
export const getDateTimeInHoursAtBudapestTZ = (date?: Date): number => {
  return getDateTimeInHoursAndMinuteAtBudapestTZ(date)[0];
};

/**
 * Checks if the given date is invalid.
 * @param dateStr - The date string to check.
 * @returns true if the date string is invalid, false otherwise.
 */
export const isInvalidDateString = (dateStr: string | Date) => {
  const date = new Date(dateStr);
  return isNaN(date.getTime());
};

export const parseExchangeDate = (dateStr: string) => {
  // For parsing this format used in exchange related dates: "2026-05-11T08:00:00"
  const parsedDate = parse(dateStr, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  if (!isValid(parsedDate)) {
    throw new Error(`Invalid exchange period date: ${dateStr}`);
  }
  return fromZonedTime(parsedDate, BudapestTimezone);
};
